import { useState } from 'react';
import axios from 'axios';
import { differenceInSeconds } from 'date-fns';

const BASE_URL = process.env.REACT_APP_DATA_ACCESS_BASE_URL;
const MB_30 = 31457280;
const MB_10 = MB_30 / 3;
const MB_60 = MB_30 * 2;
const MAX_CHUNK_SIZE = MB_30;
const CHUNK_SIZE_CUT_OFF = MAX_CHUNK_SIZE * 2;

export default function useBatchDataAccessApi() {
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState();

    const uploadFile = async (file, uploadPath) => {
        setUploading(true);
        setError(undefined);
        setProgress(0);
        let successfulUpload = true;

        try {
            const response = await axios.get(`${BASE_URL}/dataaccess?direction=upload`);

            const sasURL = new URL(response.data.sasToken);
            
            const fileRanges = [];
            if (file.size > CHUNK_SIZE_CUT_OFF) {
                console.log('Dividing file into chunks', file.size);
                const chunks = Math.ceil(file.size / MAX_CHUNK_SIZE);
                console.log('Total chunks', chunks);
                for(let i = 0; i < chunks; i++) {
                    if (i === (chunks - 1)) {
                        // last chunk, grab the rest
                        fileRanges.push({ from: i * MAX_CHUNK_SIZE, to: undefined, part: i, progress: 0 });
                    } else {
                        fileRanges.push({ from: i * MAX_CHUNK_SIZE, to: (i * MAX_CHUNK_SIZE) + MAX_CHUNK_SIZE - 1, part: i, progress: 0 });
                    }
                }

                const intervalReference = setInterval(() => {
                    const totalProgress = fileRanges.map(sc1 => sc1.progress).reduce((pv, cv) => pv + cv) / fileRanges.length;
                    setProgress(totalProgress);
                    console.log(`Progress: ${totalProgress.toFixed(2)}%`);
                }, 2000);
                
                const start = new Date();

                try {
                    while (fileRanges.some(c => c.status !== 'Uploaded' && c.status !== 'Failed')) {
                        const unuploadedChunks = fileRanges.filter(fr => fr.status !== 'Uploaded');
                        const selectedChunks = unuploadedChunks.slice(0, unuploadedChunks.length > 10 ? 10 : undefined);
                        const uploadingChunks = selectedChunks.map(sc => new Promise((resolve, _reject) => {
                            const urlParts = [
                                sasURL.origin,
                                sasURL.pathname,
                                `/chunked/${uploadPath}-part${sc.part}`,
                                sasURL.search
                            ];
    
                            const urlWithFileName = urlParts.join('');
                            sc.uploaded = 'Uploading';
                            axios.put(urlWithFileName, file.slice(sc.from, sc.to === undefined ? undefined : sc.to + 1), {
                                headers: {
                                    'Content-Type': 'application/zip',
                                    'x-ms-blob-type': 'BlockBlob'
                                },
                                onUploadProgress: (event) => {
                                    sc.progress = (event.loaded / event.total) * 100;
                                }
                            }).then(() => {
                                sc.status = 'Uploaded';
                            }).catch(e => {
                                console.error(`Failed to upload chunk ${sc.part}`, e);
                                sc.error = e;                            
                                sc.status = 'Failed';
                            }).finally(() => {
                                resolve();
                            });
                        }));    
    
                        await Promise.all(uploadingChunks);
                        
                        const failedUploads = fileRanges.filter(fr => fr.status === 'Failed');
                        if (failedUploads.length > 0) {
                            setError('Error ocurred during upload. Failed to upload file');
                            successfulUpload = false;
                            break;
                        }
                    }
                } finally {
                    const stop = new Date();
                    console.log(`Completed in ${differenceInSeconds(stop, start)}sec`);
                    clearInterval(intervalReference);
                }                
            } else {
                const urlParts = [
                    sasURL.origin,
                    sasURL.pathname,
                    `/${uploadPath}`,
                    sasURL.search
                ];
                const urlWithFileName = urlParts.join('');
                try {
                    await axios.put(urlWithFileName, file, {
                        headers: {
                            'Content-Type': 'application/zip',
                            'x-ms-blob-type': 'BlockBlob'
                        },
                        onUploadProgress: (event) => {
                            const totalProgress = (event.loaded / event.total) * 100;
                            setProgress(totalProgress);
                            console.log(`${totalProgress.toFixed(2)}%`)
                        }
                    });
                } catch (error) {
                    setError(error);
                    console.error("An error occurred while uploading to endpoint: ", error);
                    successfulUpload = false;
                }
            }
        } catch (error) {
            setError(error);
            console.error("An error occurred while retrieving the SAS URI: ", error);
            successfulUpload = false;
        } finally {
            setUploading(false);
            return successfulUpload;
        }
    }

    const downloadFile = async (batchId) => {
        setDownloading(true);
        setError(undefined);

        try {
            const response = await axios.get(`${BASE_URL}/dataaccess?direction=download`);

            const sasURL = new URL(response.data.sasToken);
            const urlParts = [
                sasURL.origin,
                sasURL.pathname,
                `/batch_${batchId}.zip`,
                sasURL.search
            ]
            const urlWithFileName = urlParts.join('');

            try {
                const downloadResponse = await axios.get(urlWithFileName, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'x-ms-blob-type': 'BlockBlob'
                    },
                    responseType: 'blob'
                });

                const fileBlob = downloadResponse.data;

                return fileBlob;
            } catch (error) {
                setError(error);
                if (error.response && error.response.status === 404) {
                    console.error("BatchID does not exist: ", batchId);
                }
                console.error("An error occurred while uploading to endpoint: ", error);
                return null;
            }
        } catch (error) {
            setError(error);
            console.error("An error occurred while retrieving the SAS URI: ", error);
            return null;
        } finally {
            setDownloading(false);
        }
    }

    return {
        download: downloadFile,
        upload: uploadFile,
        progress,
        uploading,
        downloading,
        error
    };
}
